import { Divider, Drawer, Grid, Typography, Checkbox } from '@material-ui/core';
import {
  ConviteModelRequest,
  FuncaoInternaParticipante,
  FuncaoModuloResponse,
  FuncaoParticipante,
  GrupoAcesso,
  Modulos,
  ModuloUsuarioRequest,
  ModuloUsuarioResponse,
  ParticipanteFuncaoResponse,
  ParticipanteFuncaoUsuarioResponse,
  ParticipanteResponse,
  Perfil,
  UsuarioEditRequest,
  UsuarioModelResponse,
} from 'common/genericTypes';
import { Dropdown, SmSpace, TextField, Button, RadioButton } from 'm2m.components';
import { useCallback, useEffect, useState } from 'react';
import { perfilOptions, grupoAcessoOptions, Option } from 'common/genericOptions';
import { validaEmail } from 'utils/validation';
import { RootState } from "store";
import { useDispatch, useSelector } from "react-redux";
import React from 'react';
import { addAlert } from 'store/modules/snackBar';

interface Props {
  participantes: Array<ParticipanteResponse>;
  open: boolean;
  usuario: UsuarioModelResponse;
  onClose: () => void;
}

type FuncaoType = FuncaoParticipante | FuncaoInternaParticipante;

const statusOptions: Option[] = [
  {
    description: 'Ativo',
    value: 'ATIVO'
  },
  {
    description: 'Inativo',
    value: 'INATIVO'
  }
];

export const EditarUsuario: React.FC<Props> = (props) => {
  const { participantes, usuario, open, onClose } = props;

  const [email, setEmail] = useState<string>('');
  const [nome, setNome] = useState<string>('');
  const [grupoAcesso, setGrupoAcesso] = useState<GrupoAcesso>(GrupoAcesso.CadastroLeitura);
  const [ativo, setAtivo] = useState<boolean>(true);
  const [perfis, setPerfis] = useState<Array<{ funcao: FuncaoType, perfil: Perfil }>>([]);

  const [modulos, setModulos] = useState<Array<FuncaoModuloResponse>>([]);
  const [funcoesParticipantesInfo, setFuncoesParticipantesInfo] = useState<Array<ParticipanteFuncaoUsuarioResponse>>([]);
  const [participante, setParticipante] = useState<ParticipanteResponse | null>(null);

  const isUsuarioGestaoAcessos = useSelector((state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.GestaoAcessos);
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);
  const getModulosLimiteApi = useSelector((state: RootState) => state.usuarioApi.postModulosLimite);
  const getModulosUsuarioApi = useSelector((state: RootState) => state.usuarioApi.getModulosUsuario);
  const getParticipanteApi = useSelector((state: RootState) => state.participanteApi.getParticipante);
  const putUsuario = useSelector((state: RootState) => state.usuarioApi.putUsuario);

  const isCadastroValido = usuario
    ? !!(email && validaEmail(email) && nome && funcoesParticipantesInfo.length)
    : !!(nome && email && validaEmail(email) && perfis.length > 0);

  const dispatch = useDispatch();

  const getModulosLimite = useCallback(async (funcoes: Array<FuncaoParticipante | FuncaoInternaParticipante>) => {
    try {
      const result = await getModulosLimiteApi(dispatch, { funcoes: funcoes, habilitado: false }, true);
      return result;
    } catch (error) { }
    return [];
  }, []);

  const getParticipante = useCallback(async () => {
    try {
      const result = await getParticipanteApi(dispatch, participanteId, true);
      setParticipante(result);
    } catch (error) { }
  }, [participanteId]);

  const getModulosUsuario = useCallback(async () => {
    try {
      const result = await getModulosUsuarioApi(dispatch, usuario.id, true);
      return result;
    } catch (error) { }
    return [];
  }, []);

  const initializeModulos = async (funcoes: Array<FuncaoParticipante | FuncaoInternaParticipante>) => {
    const modulosLimite = await getModulosLimite(funcoes);
    const modulosUsuario = await getModulosUsuario();
    updateModulosUsuario(modulosUsuario, modulosLimite);
  }

  const updateModulosUsuario = (modulosUsuario: FuncaoModuloResponse[], modulosLimite: FuncaoModuloResponse[]) => {
    const modulosUsuarioCopy = [...modulosUsuario];
    
    const funcoesUsuarioSemModulos = usuario.participanteFuncoes.filter(pf => 
      !modulosUsuarioCopy.some(m => m.funcao === pf.funcao)
    );

    funcoesUsuarioSemModulos.forEach(funcaoUsuario => {
      const modulosParaFuncao = modulosLimite.find(ml => ml.funcao === funcaoUsuario.funcao);
      if (modulosParaFuncao) {
        modulosUsuarioCopy.push({
          funcao: funcaoUsuario.funcao,
          modulos: [...modulosParaFuncao.modulos]
        });
      }
    });

    modulosUsuarioCopy.forEach(moduloUsuario => {
      const modulosLimiteParaFuncao = modulosLimite.find(ml => ml.funcao === moduloUsuario.funcao);
      
      if (modulosLimiteParaFuncao) {
        const modulosUsuarioMap = new Map<string, ModuloUsuarioResponse>();
        moduloUsuario.modulos.forEach(m => modulosUsuarioMap.set(m.nomeEnum, m));
        
        const modulosAtualizados: ModuloUsuarioResponse[] = [];
        
        modulosLimiteParaFuncao.modulos.forEach(moduloLimite => {
          const moduloExistente = modulosUsuarioMap.get(moduloLimite.nomeEnum);
          
          if (moduloExistente) {
            modulosAtualizados.push(moduloExistente);
          } else {
            modulosAtualizados.push({
              ...moduloLimite,
              habilitado: false
            });
          }
        });
        moduloUsuario.modulos = modulosAtualizados;
      }
      moduloUsuario.modulos.sort((a, b) => a.id - b.id);
    });
    
    setModulos(modulosUsuarioCopy);
  }

  const handlePerfilChange = (funcao: FuncaoType, perfil: Perfil) => {
    let newPerfis = perfis.map(p => {
      return (p.funcao === funcao) ? { ...p, perfil } : p;
    });

    if (!newPerfis.some(np => np.funcao === funcao))
      newPerfis = [...newPerfis, { funcao, perfil }];

    setPerfis(newPerfis);
  }

  const handleSubmit = async () => {
    try {
      const usuarioRequest: UsuarioEditRequest = {
        id: usuario.id,
        nome: nome,
        grupoAcesso: isUsuarioGestaoAcessos ? grupoAcesso : GrupoAcesso.Comum,
        ativo: ativo,
        funcaoParticipanteUsuarios: funcoesParticipantesInfo.map((pf) => {
          const updatedPerfil = perfis.find(p => pf.funcao == p.funcao);
          const updatedModulos = modulos.find(m => m.funcao == pf.funcao);

          return {
            participanteFuncaoId: pf.participanteFuncaoId,
            perfil: updatedPerfil?.perfil ?? pf.perfil,
            moduloUsuario: updatedModulos?.modulos.map(m => ({ id: m.id, habilitado: m.habilitado })) ?? []
          }
        })
      };

      await putUsuario(dispatch, usuarioRequest, true);
      onClose();
      dispatch(
        addAlert({
          message: `O usuário ${usuarioRequest.nome} foi editado com sucesso.`,
          severity: 'success',
        })
      );
    }
    catch (error) { }
  }

  const handleClose = () => {
    setNome('');
    setEmail('');
    setAtivo(true);
    setPerfis([]);
    setGrupoAcesso(GrupoAcesso.CadastroLeitura);
    setFuncoesParticipantesInfo([]);
    setModulos([]);
    setParticipante(null);

    onClose();
  };

  useEffect(() => {
    setNome(usuario.nome);
    setEmail(usuario.email);
    setPerfis(usuario.participanteFuncoes.map(pf => ({ funcao: pf.funcao, perfil: pf.perfil })));
    setGrupoAcesso(usuario.grupoAcesso);
    setAtivo(usuario.ativo);
    setFuncoesParticipantesInfo([...usuario.participanteFuncoes]);
  }, [usuario]);

  const changeModulos = (moduloId: number, funcao: FuncaoParticipante | FuncaoInternaParticipante) => {
    const newModulos = [...modulos];

    const moduleToUpdate = newModulos.find(m => m.funcao == funcao);

    moduleToUpdate?.modulos.forEach(modulo => {
      if (modulo.id == moduloId) {
        modulo.habilitado = !modulo.habilitado;
      }
    })

    setModulos(newModulos);
  };

  useEffect(() => {
    if (participantes.length > 0) {
      const result = participantes.find(p => p.id === participanteId);
      setParticipante(result ?? null);
    } else {
      getParticipante();
    }
  }, [participanteId, participantes]);

  useEffect(() => {
    if (participante)
      initializeModulos(participante.funcoes.map(f => f.funcao));
    else
      setModulos([]);
  }, [participante]);

  const PerfilComponent = (funcao: FuncaoType) => {
    const perfilData = perfis.find(p => p.funcao === funcao);

    return (
      <Dropdown
        label={`Perfil`}
        required
        options={perfilOptions}
        value={perfilData?.perfil ?? Perfil.Leitura}
        onChange={(value) => handlePerfilChange(funcao, value as Perfil)}
      />
    );
  }

  const ListaModulos = (funcao: FuncaoParticipante | FuncaoInternaParticipante) => {
    return (
      <Grid container>
        <Typography variant="body1">{"Módulos"}</Typography>
        <SmSpace />
        {modulos?.find(mo => mo.funcao == funcao)?.modulos.map((modulo: ModuloUsuarioResponse, index) => (
          <Grid key={modulo.id} item md={12}>
            <Checkbox
              color={'primary'}
              checked={modulo.habilitado}
              onChange={() => {
                changeModulos(modulo.id, funcao);
              }}
            />
            {modulo.nome}
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Drawer anchor="right" open={open} onClose={handleClose} PaperProps={{ style: { padding: '40px', width: 520 } }}>
      <Grid container>
        <Grid item md={12}>
          <Typography variant="h3">{`${usuario ? 'Editar' : 'Novo'} Usuário`}</Typography>
          <SmSpace />
          <Divider variant="fullWidth" />
          <SmSpace />
          <TextField label="Nome" required value={nome} onChange={setNome} />
          <SmSpace />
          <TextField
            label="E-mail *"
            value={email}
            onChange={setEmail}
            error={!!email && !usuario && !validaEmail(email)}
            errorMessage={'E-mail inválido'}
            disabled={!!usuario}
          />
          <SmSpace />
          {isUsuarioGestaoAcessos && <>
            <Dropdown
              label={`Grupo de Acesso`}
              required
              options={grupoAcessoOptions}
              value={grupoAcesso}
              onChange={(value) => setGrupoAcesso(value as GrupoAcesso)}
            />
            <SmSpace />
          </>}
          <RadioButton
            label={'Status'}
            options={statusOptions}
            value={ativo ? 'ATIVO' : 'INATIVO'}
            onChange={(value: string) => setAtivo(value === 'ATIVO')}
            inline
          />
          <SmSpace />
          <Divider variant="fullWidth" />
          <SmSpace />
          {usuario.participanteFuncoes.map((usuario, index) =>
            <React.Fragment key={index}>
              <Typography variant='body2'> {`Função: ${usuario.funcao}`}</Typography>
              <SmSpace />
              {PerfilComponent(usuario.funcao)}
              <SmSpace />
              {ListaModulos(usuario.funcao)}
              <SmSpace />
            </React.Fragment>

          )}
          <SmSpace />
          <Divider variant="fullWidth" />
          <SmSpace />
          <SmSpace />
          <Button onClick={handleSubmit} variant={'contained'} color={'primary'} fullWidth disabled={!isCadastroValido}>
            {`Salvar Usuário`}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );
};
